import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import {
    useActiveEditTarget,
    useShownSlicePlane,
} from '~/hooks/target/useActiveEditTarget';
import type { PlaneMm } from '~/domain/geometry/patientGeometry';
import { enqueueSnackbar } from '~/utils/notifications';
import {
    type SliceRefusalMessageId,
    type SliceRefusalRemedy,
    sliceRefusalMessageId,
} from './sliceRefusal';

export interface ShownSlicePlaneOrRefusal {
    /** The acquired slice a tile of the edit target shows; `null` when none. */
    plane: PlaneMm | null;
    /** Fresh read of {@link plane} at call time. */
    readPlane: () => PlaneMm | null;
    /** Why there is no shown slice to act on, for a readout beside the control. */
    refusalMessageId: SliceRefusalMessageId;
    /**
     * Fresh read of the shown slice at the moment an operation commits.
     * With none shown, states {@link refusalMessageId} as a warning and
     * returns `null`: the caller acts on nothing.
     */
    readPlaneOrRefuse: () => PlaneMm | null;
}

/**
 * The acquired slice a tile of the edit target shows, for a single-slice
 * operation whose whole-extent alternative is `remedy`, with the refusal
 * that operation states while no tile shows it.
 */
export function useShownSlicePlaneOrRefusal(
    remedy: SliceRefusalRemedy
): ShownSlicePlaneOrRefusal {
    const intl = useIntl();
    const { environment } = useActiveEditTarget();
    const { plane, readPlane } = useShownSlicePlane();
    const refusalMessageId = sliceRefusalMessageId({
        tilted: !!environment?.image?.parent.firstSlice.tilted,
        addressedBy: 'acquisition',
        remedy,
    });

    const readPlaneOrRefuse = useCallback((): PlaneMm | null => {
        const shownPlane = readPlane();
        if (shownPlane === null) {
            enqueueSnackbar(intl.formatMessage({ id: refusalMessageId }), {
                variant: 'warning',
            });
        }
        return shownPlane;
    }, [intl, readPlane, refusalMessageId]);

    return { plane, readPlane, refusalMessageId, readPlaneOrRefuse };
}
